import React from 'react';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import Link from '@material-ui/core/Link';
import Container from 'common/Container';
import PriceVatDisclaimer from 'common/PriceVatDisclaimer';
import { useTheme } from '@material-ui/core/styles';

const products = [
  {
    id: 'vivir-en-equilibrio',
    overline: 'Programa Ancla',
    title: 'Vivir en Equilibrio con Liliana Fernandes',
    text: 'El vehículo perfecto para humanizar la marca y conectar desde el bienestar 360 grados.',
    impacts: [
      'Contenido orgánico: Smart Training como centro de soluciones para el equilibrio integral.',
      'Menciones de estilo de vida que posicionan el gimnasio como espacio de transformación y disciplina.',
      '3 menciones semanales integradas estratégicamente al contenido.',
    ],
  },
  {
    id: 'digest',
    overline: 'Motor de Conversión',
    title: 'TNO Digest (SEO y Autoridad)',
    text: 'Centro de inteligencia digital para captar nuevos miembros.',
    impacts: [
      '2 reseñas especializadas al mes sobre servicios o clases específicas del gimnasio.',
      '1 artículo mensual de tendencias fitness con enlaces directos a inscripción.',
      'Periodismo experto en SEO para liderar búsquedas en Google.',
      'Estrategia de afiliados con enlaces rastreables a planes o contacto de ventas.',
    ],
  },
  {
    id: 'streaming-ondemand',
    overline: 'Visibilidad Integral',
    title: 'Streaming, On-Demand y Presencia Digital',
    text: 'Cobertura de marca en todo el ecosistema de TNO Network.',
    impacts: [
      'Streaming 24/7 con 12 spots diarios de 20 segundos en streaming.',
      '2 cápsulas mensuales tipo Smart Tips grabadas en las sedes.',
      'Banner de aliado en el portal principal de TNO Network.',
      'Frecuencia constante para recordación y conversión.',
    ],
  },
];

const investmentRows = [
  { product: 'Streaming 24/7', detail: '12 spots diarios (20 seg) en señal de streaming' },
  { product: 'Vivir en Equilibrio con Liliana Fernandes', detail: '3 menciones semanales integradas al contenido' },
  { product: 'TNO Digest (SEO Premium)', detail: '2 reseñas al mes + 1 artículo de tendencias con enlaces directos' },
  { product: 'On-Demand', detail: '2 cápsulas mensuales tipo Smart Tips grabadas en sedes' },
  { product: 'Presencia Digital', detail: 'Banner de aliado en portal principal de TNO Network' },
];

const valueAdd = [
  'Trabajamos con mística y cercanía para construir alianzas reales.',
  'Cuidamos a Smart Training como parte de nuestra familia, con rigor técnico y calidez humana.',
  'Somos el medio disruptivo para un centro que redefine los límites del fitness.',
];

const LandingMediaKitSmartTraining = ({ themeMode = 'light' }) => {
  const theme = useTheme();
  const accentColor = theme.palette.brand?.smartTrainingRed || '#C62828';
  const SHOW_SLUG = 'vivirenequilibrio';
  const FILES_BASE = 'https://files.tnonetwork.com';
  const showUrl = `https://tnonetwork.com/show/${SHOW_SLUG}`;
  const coverUrl = `${FILES_BASE}/shows/cover/${SHOW_SLUG}.jpg`;
  const logoUrl = `${FILES_BASE}/shows/logo/${SHOW_SLUG}.png`;
  const anchorImageUrl = `${FILES_BASE}/users/talent/lilianafernandes.jpg`;
  const whatsappMessage =
    'Hola, me interesa la propuesta estratégica Smart Training x TNO Network (Plan Equilibrio Smart).';

  return (
    <Box>
      <Container>
        <Box marginBottom={0}>
          <Container
            sx={{
              backgroundImage:
                themeMode === 'dark'
                  ? 'url(/images/logos/logo_white.PNG)'
                  : 'url(/images/logos/logo_tnonetwork_black.png)',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              backgroundSize: 'contain',
              height: '16vh',
            }}
          />
          <Typography
            sx={{ textTransform: 'uppercase', fontWeight: 600 }}
            variant="overline"
            color="textSecondary"
            align="center"
            display="block"
            gutterBottom
          >
            Propuesta Estratégica
          </Typography>
          <Box
            component={Typography}
            fontWeight={700}
            variant="h3"
            gutterBottom
            align="center"
            color={theme.palette.primary.main}
          >
            Smart Training x TNO Network
          </Box>
          <Typography
            variant="h6"
            component="p"
            color="textSecondary"
            align="center"
            sx={{ fontStyle: 'italic', mb: 3 }}
          >
            Inteligencia en Movimiento: La Evolución del Entrenamiento
          </Typography>
          <Box
            sx={{
              maxWidth: 820,
              mx: 'auto',
              py: 2,
              px: 2,
              borderRadius: 2,
              bgcolor: theme.palette.mode === 'dark' ? 'action.hover' : 'grey.50',
              borderLeft: `5px solid ${accentColor}`,
            }}
          >
            <Typography variant="h6" component="p" color="textSecondary" align="center" gutterBottom>
              <strong>1. El Valor de la Alianza</strong>
            </Typography>
            <Typography variant="body1" color="textSecondary" align="center">
              Smart Training representa la nueva era del fitness, donde la metodología y el rendimiento se unen. TNO Network, como plataforma pionera en streaming, es el socio ideal para proyectar esa modernidad con un despliegue visual de alto impacto que conecta con una audiencia activa y digital.
            </Typography>
            <Box display="flex" justifyContent="center" sx={{ mt: 2 }}>
              <Box
                component="img"
                src="/images/smarttraining.jpg"
                alt="Smart Training"
                sx={{
                  width: '100%',
                  maxWidth: 240,
                  height: 'auto',
                  objectFit: 'contain',
                }}
              />
            </Box>
          </Box>
        </Box>
      </Container>

      <Container>
        <Box>
          <Box marginBottom={4}>
            <Typography sx={{ textTransform: 'uppercase', fontWeight: 'medium' }} gutterBottom color="primary" align="center">
              Programa Ancla
            </Typography>
            <Box component={Typography} fontWeight={700} variant="h4" gutterBottom align="center" color="primary">
              Vivir en Equilibrio con Liliana Fernandes
            </Box>
            <Typography align="center" sx={{ mb: 1 }}>
              <Link href={showUrl} target="_blank" rel="noopener noreferrer" color="primary" underline="hover">
                {`tnonetwork.com/show/${SHOW_SLUG}`}
              </Link>
            </Typography>
            <Typography variant="body1" color="textSecondary" align="center" sx={{ maxWidth: 780, mx: 'auto' }}>
              Este espacio es el vehículo perfecto para humanizar la marca y conectar
              desde el bienestar 360 grados. Smart Training se integra como centro de
              soluciones para quienes buscan equilibrio físico, mental y disciplina.
            </Typography>
          </Box>

          <Grid container spacing={4} alignItems="stretch">
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: 3,
                  height: '100%',
                  borderRadius: 2,
                  borderLeft: `5px solid ${accentColor}`,
                  bgcolor: theme.palette.mode === 'dark' ? 'action.hover' : 'grey.50',
                }}
              >
                <Typography variant="h6" gutterBottom color="primary" fontWeight={700}>
                  Integración orgánica
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  Liliana presenta a Smart Training como el punto de referencia para
                  una vida saludable y sostenible, destacando instalaciones, metodología
                  y acompañamiento como herramientas clave para alcanzar resultados.
                </Typography>
                <Typography variant="h6" gutterBottom color="primary" fontWeight={700}>
                  Menciones de estilo de vida
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Recomendaciones editoriales que posicionan al gimnasio como un espacio
                  de transformación personal, constancia y alto rendimiento, alineado con
                  los valores del programa.
                </Typography>
                <Box mt={2}>
                  <Typography variant="h6" gutterBottom color="primary" fontWeight={700}>
                    Narrativa editorial sugerida
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    <Typography component="li" variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                      Entrenar con método: cómo convertir disciplina en resultados medibles.
                    </Typography>
                    <Typography component="li" variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                      Bienestar 360: equilibrio entre rendimiento físico, foco mental y recuperación.
                    </Typography>
                    <Typography component="li" variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                      Smart routines: recomendaciones accionables para audiencia activa y digital.
                    </Typography>
                    <Typography component="li" variant="body2" color="textSecondary">
                      Comunidad y pertenencia: el gimnasio como espacio de transformación sostenible.
                    </Typography>
                  </Box>
                </Box>
                <Box mt={2}>
                  <Typography variant="h6" gutterBottom color="primary" fontWeight={700}>
                    Perfil de audiencia
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Adultos jóvenes y profesionales que priorizan salud, productividad y
                    constancia. Buscan soluciones prácticas para entrenar mejor, sostener
                    hábitos y elevar su calidad de vida.
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: 3,
                  height: '100%',
                  borderRadius: 2,
                  borderLeft: `5px solid ${accentColor}`,
                  position: 'relative',
                  overflow: 'hidden',
                  backgroundImage: `url(${coverUrl})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  backgroundColor: 'rgba(0, 0, 0, 0.35)',
                  backgroundBlendMode: 'multiply',
                }}
              >
                <Box
                  sx={{
                    maxWidth: 340,
                    mx: 'auto',
                    p: 2.5,
                    borderRadius: 2,
                    backgroundColor: 'rgba(0, 0, 0, 0.35)',
                    backdropFilter: 'blur(2px)',
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      minHeight: 88,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Box
                      component="img"
                      src={logoUrl}
                      alt="Logo Vivir en Equilibrio"
                      sx={{
                        maxWidth: '72%',
                        height: 'auto',
                        objectFit: 'contain',
                      }}
                    />
                  </Box>
                  <Box mt={2} textAlign="center">
                    <Box
                      component="img"
                      src={anchorImageUrl}
                      alt="Liliana Fernandes"
                      sx={{
                        width: 120,
                        height: 120,
                        borderRadius: '50%',
                        objectFit: 'cover',
                        boxShadow: 2,
                      }}
                    />
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 1, color: 'common.white' }}>
                      Liliana Fernandes
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.92)' }}>
                      Ancla de Vivir en Equilibrio
                    </Typography>
                  </Box>
                  <Box mt={2} textAlign="center">
                    <Typography variant="h6" sx={{ color: 'common.white' }} gutterBottom>
                      Activación semanal
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.92)' }} paragraph>
                      3 menciones integradas para elevar confianza y conversión.
                    </Typography>
                  </Box>
                  <Box mt={2}>
                    <Typography
                      variant="overline"
                      sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 700 }}
                      display="block"
                      gutterBottom
                    >
                      Entregables del programa
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                      <Typography component="li" variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.92)', mb: 0.5 }}>
                        Integración orgánica de marca en contexto de bienestar.
                      </Typography>
                      <Typography component="li" variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.92)', mb: 0.5 }}>
                        Llamados a la acción orientados a inscripción y contacto comercial.
                      </Typography>
                      <Typography component="li" variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.92)', mb: 0.5 }}>
                        Refuerzo de autoridad con tono cercano y validación editorial.
                      </Typography>
                      <Typography component="li" variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.92)' }}>
                        Mensajes alineados con clases, metodología y propuesta diferencial.
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Container>

      <Container>
        <Box marginBottom={4}>
          <Typography sx={{ textTransform: 'uppercase', fontWeight: 'medium' }} gutterBottom color="primary" align="center">
            Plan de Inversión
          </Typography>
          <Box component={Typography} fontWeight={700} variant="h4" gutterBottom align="center" color="primary">
            Equilibrio Smart
          </Box>
        </Box>
        <Grid container spacing={4}>
          {products.map((item, i) => (
            <Grid item xs={12} md={4} key={item.id}>
              <Card
                elevation={2}
                sx={{
                  height: '100%',
                  borderTop: `5px solid ${accentColor}`,
                }}
                data-aos="fade-up"
                data-aos-delay={i * 80}
              >
                <CardContent>
                  <Typography variant="overline" color="primary" fontWeight={600} display="block" gutterBottom>
                    {item.overline}
                  </Typography>
                  <Typography variant="h6" gutterBottom fontWeight={700}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" paragraph>
                    {item.text}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    Impacto:
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5, mt: 0.5 }}>
                    {item.impacts.map((impact) => (
                      <Typography key={impact} component="li" variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                        {impact}
                      </Typography>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Container>
        <Box marginBottom={4}>
          <Typography sx={{ textTransform: 'uppercase', fontWeight: 'medium' }} gutterBottom color="primary" align="center">
            5. Por qué TNO
          </Typography>
          <Box component={Typography} fontWeight={700} variant="h4" gutterBottom align="center" color="primary">
            El Diferencial para Smart Training
          </Box>
        </Box>
        <Grid container spacing={3}>
          {valueAdd.map((item, i) => (
            <Grid item xs={12} md={4} key={item}>
              <Box
                sx={{
                  p: 2,
                  height: '100%',
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  bgcolor: theme.palette.mode === 'dark' ? 'action.hover' : 'grey.50',
                  borderLeft: `5px solid ${accentColor}`,
                }}
                data-aos="fade-up"
                data-aos-delay={i * 80}
              >
                <Typography variant="body2" color="textSecondary">
                  {item}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Container>
        <Box marginBottom={4}>
          <Typography sx={{ textTransform: 'uppercase', fontWeight: 'medium' }} gutterBottom color="primary" align="center">
            Resumen
          </Typography>
          <Box component={Typography} fontWeight={700} variant="h4" gutterBottom align="center" color="primary">
            Resumen de Inversión
          </Box>
        </Box>
        <TableContainer component={Paper} elevation={2} sx={{ maxWidth: 900, mx: 'auto', borderRadius: 2, borderTop: `5px solid ${accentColor}` }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: theme.palette.primary.main }}>
                <TableCell sx={{ color: 'white', fontWeight: 700 }}>Producto</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700 }}>Detalle</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {investmentRows.map((row) => (
                <TableRow key={row.product} hover>
                  <TableCell>{row.product}</TableCell>
                  <TableCell>{row.detail}</TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ bgcolor: theme.palette.primary.light }}>
                <TableCell sx={{ fontWeight: 700, color: 'white' }}>
                  INVERSIÓN MENSUAL TOTAL
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: 'white' }}>
                  $160
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <PriceVatDisclaimer sx={{ maxWidth: 900 }} />
      </Container>

      <Container>
        <Box component={Typography} sx={{ fontWeight: 700 }} variant="h4" gutterBottom align="center" marginBottom={2} color={theme.palette.primary.main}>
          Activemos Equilibrio Smart
        </Box>
        <Typography variant="h6" align="center" color="textSecondary" sx={{ mb: 4, maxWidth: 760, mx: 'auto' }}>
          Convirtamos la propuesta de Smart Training en una narrativa de bienestar, autoridad y conversión sostenible dentro del ecosistema TNO.
        </Typography>
        <Box sx={{ width: 72, height: 4, bgcolor: accentColor, borderRadius: 999, mx: 'auto', mb: 3 }} />
        <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="center" gap={2}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={() => {
              const url = `https://wa.me/584123964576?text=${encodeURIComponent(whatsappMessage)}`;
              window.open(url, '_blank');
            }}
          >
            Activar propuesta Smart Training
          </Button>
          <Button
            href="https://www.tnonetwork.com"
            variant="outlined"
            color="primary"
            size="large"
            target="_blank"
            rel="noopener noreferrer"
          >
            Conocer TNO Network
          </Button>
        </Box>
        <Box marginTop={4} textAlign="center">
          <Typography variant="body2" color="textSecondary">
            © TNO Network 2026 | Smart Training x TNO Network | The Content Evolution.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

LandingMediaKitSmartTraining.propTypes = {
  themeMode: PropTypes.string,
};

export default LandingMediaKitSmartTraining;
